import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Trash2, ExternalLink } from "lucide-react";
import type { NotificationSettings } from "@/hooks/useNotificationSettings";
import { supabase } from "@/integrations/supabase/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface JiraConfigProps {
  settings: NotificationSettings;
  onSave: (updates: Partial<NotificationSettings>) => Promise<void>;
}

export function JiraConfig({ settings, onSave }: JiraConfigProps) {
  const [jiraUrl, setJiraUrl] = useState(settings.jira_url || "");
  const [apiKey, setApiKey] = useState(settings.jira_api_key || "");
  const [projectKey, setProjectKey] = useState(settings.jira_project_key || "");
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testSent, setTestSent] = useState(false);

  useEffect(() => {
    setJiraUrl(settings.jira_url || "");
    setApiKey(settings.jira_api_key || "");
    setProjectKey(settings.jira_project_key || "");
  }, [settings]);

  const handleSave = async () => {
    if (!jiraUrl || !apiKey || !projectKey) {
      toast.error("All fields are required to connect Jira");
      return;
    }
    try {
      await onSave({
        jira_url: jiraUrl || null,
        jira_api_key: apiKey || null,
        jira_project_key: projectKey || null,
        jira_enabled: jiraUrl ? true : false
      });
      toast.success("Jira settings saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save Jira settings");
    }
  };

  const handleDisconnect = async () => {
    try {
      await onSave({ jira_url: null, jira_api_key: null, jira_project_key: null, jira_enabled: false });
      setJiraUrl(""); setApiKey(""); setProjectKey(""); setVerified(false); setTestSent(false);
      toast.success("Jira credentials purged.");
    } catch {
      toast.error("Failed to disconnect.");
    }
  };

  const handleVerify = async () => {
    if (!jiraUrl || !apiKey || !projectKey) return toast.error("Fill all fields first");
    setVerifying(true);
    setVerified(false);
    try {
      // Offline pattern verification to quickly validate connection URL structure
      if (!jiraUrl.startsWith("https://") || !jiraUrl.includes("atlassian.net")) {
        throw new Error("Invalid Jira instance URL. Must be a valid Atlassian Cloud URL.");
      }
      setVerified(true);
      toast.success("Jira connection validated");
    } catch (e: any) {
      toast.error(e.message || "Failed to verify connection parameters");
    } finally {
      setVerifying(false);
    }
  };

  const handleSendTestAlert = async () => {
    if (!jiraUrl || !apiKey || !projectKey) return toast.error("Connect and verify configuration first");
    setSendingTest(true);
    setTestSent(false);
    try {
      const { data, error } = await supabase.functions.invoke("test-integration", {
        body: { type: "jira", url: jiraUrl, api_key: apiKey, project_key: projectKey },
      });
      if (error || (data && data.error)) {
        toast.error(error?.message || data?.error || "Failed to send test alert to Jira");
      } else {
        setTestSent(true);
        toast.success("Test alert sent to Jira");
      }
    } catch {
      toast.error("Failed to reach Jira instance for testing");
    } finally {
      setSendingTest(false);
    }
  };

  const isConnected = !!(jiraUrl && apiKey && projectKey);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-black uppercase tracking-tight">Jira Integration</p>
          <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">Auto-create Jira tickets when incidents are triggered</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Setup Guide */}
        <div className="border-2 border-primary/20 bg-primary/5 p-4 rounded-none space-y-3 group hover:border-primary transition-colors">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="text-[10px] font-black uppercase">Quick Setup Guide</span>
          </div>
          <div className="bg-muted/30 border border-foreground/10 p-3 text-[10px] font-bold uppercase tracking-tight text-muted-foreground space-y-1.5">
            <p className="text-foreground font-black">Follow these steps:</p>
            <p className="flex gap-2"><span>1.</span> Log into your Jira Cloud account.</p>
            <p className="flex gap-2"><span>2.</span> Go to Account Settings → Security → API Tokens.</p>
            <p className="flex gap-2"><span>3.</span> Click "Create API Token" and copy it.</p>
            <p className="flex gap-2"><span>4.</span> Note your project key (e.g. OPS, ENG, WATCH).</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-none border-2 border-foreground bg-background font-black uppercase text-[9px] gap-1.5"
            onClick={() => window.open("https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/", "_blank")}
          >
            <ExternalLink className="h-3 w-3" />
            Atlassian Docs
          </Button>
        </div>

        {/* Jira Fields */}
        <div className="border-2 border-border p-4 rounded-none space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-black uppercase opacity-60">Jira Instance URL</Label>
            <Input
              value={jiraUrl}
              onChange={(e) => { setJiraUrl(e.target.value); setVerified(false); setTestSent(false); }}
              placeholder="https://your-org.atlassian.net"
              className="h-9 rounded-none border-2 border-foreground font-mono text-[10px] focus-visible:ring-primary shadow-brutal-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-black uppercase opacity-60">API Token</Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setVerified(false); setTestSent(false); }}
              placeholder="••••••••••••••••••••"
              className="h-9 rounded-none border-2 border-foreground font-mono text-[10px] focus-visible:ring-primary shadow-brutal-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-black uppercase opacity-60">Project Key</Label>
            <Input
              value={projectKey}
              onChange={(e) => { setProjectKey(e.target.value.toUpperCase()); setVerified(false); setTestSent(false); }}
              placeholder="OPS"
              className="h-9 rounded-none border-2 border-foreground font-mono text-[10px] focus-visible:ring-primary shadow-brutal-sm uppercase"
            />
          </div>
          {isConnected && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-[9px] font-black uppercase text-status-down hover:bg-status-down/10 gap-1.5 h-8">
                  <Trash2 className="h-3 w-3" /> Disconnect Jira
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="glass-strong rounded-none border-4 border-foreground bg-background shadow-brutal p-6">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-base font-black uppercase tracking-tight">Disconnect Jira</AlertDialogTitle>
                  <AlertDialogDescription className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-2">
                    This will purge all Jira credentials. No further tickets will be created automatically.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="mt-4 gap-2 flex-row justify-end">
                  <AlertDialogCancel className="rounded-none border-2 border-foreground bg-background text-[10px] font-black uppercase tracking-widest shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all mt-0">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDisconnect} className="rounded-none border-2 border-foreground bg-status-down text-white hover:bg-foreground hover:text-background font-black uppercase text-[10px] shadow-[2px_2px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all">
                    Disconnect
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Button onClick={handleSave} className="h-9 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase text-[10px] shadow-brutal-sm hover:bg-primary transition-all">
          Save Configuration
        </Button>
        <Button
          variant="outline"
          onClick={handleVerify}
          disabled={verifying || !isConnected}
          className="h-9 rounded-none border-2 border-foreground bg-background font-black uppercase text-[10px] shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
        >
          {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          {verifying ? "Verifying..." : "Verify Connection"}
        </Button>
        {verified && (
          <Button
            variant="outline"
            onClick={handleSendTestAlert}
            disabled={sendingTest}
            className="h-9 rounded-none border-2 border-primary bg-primary/10 font-black uppercase text-[10px] shadow-brutal-sm hover:bg-primary/20 transition-all"
          >
            {sendingTest ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            {sendingTest ? "Sending Alert..." : "Send Test Alert"}
          </Button>
        )}
        {verified && (
          <div className="flex items-center gap-1.5 ml-auto bg-status-up/10 px-3 py-1 border-2 border-status-up shadow-brutal-sm">
            <CheckCircle2 className="h-3 w-3 text-status-up" />
            <span className="text-[10px] font-black text-status-up uppercase">
              {testSent ? "Test Sent" : "Verified"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
